import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";

import { MDSText, MDSTextColor, MDSTextVariant, MDSTextWeight } from "@/mds";
import { AgentBackLink } from "@/pages/agent/components/agent-back-link";
import { AgentLinkButton } from "@/pages/agent/components/agent-link-button";
import { AgentLinkedTaskList } from "@/pages/agent/components/agent-linked-task-list";
import { AgentMetaCard } from "@/pages/agent/components/agent-meta-card";
import { AgentPageLayout } from "@/pages/agent/components/agent-page-layout";
import {
  AgentRelationshipCard,
  AgentRelationshipLabel,
  AgentRelationshipSection,
} from "@/pages/agent/components/agent-relationship-card";
import { AgentSourceNoteCard } from "@/pages/agent/components/agent-source-note-card";
import { AgentSurfaceNavigationActiveTab } from "@/pages/agent/components/agent-surface-navigation";
import { useAgentIntentGraph } from "@/pages/agent/intent-graph/use-agent-intent-graph";
import { formatAgentDate } from "@/pages/agent/utils/agent-date.utils";
import { windowModule } from "@/modules/window";
import { useAppStore } from "@/store";
import {
  isClosedAssistantTaskStatus,
  normalizeAssistantTaskStatus,
} from "@/store/assistant/assistant-task-status";
import { AssistantTaskStatus } from "@/store/assistant/types";

export const AgentInformationItemPage = observer(
  ({ informationItemId }: { informationItemId: string }) => {
    const { store } = useAppStore();
    const graph = useAgentIntentGraph();
    const item = graph.data?.information_items?.find(value => value.id === informationItemId);
    const note =
      item?.kind === "note"
        ? store.notes.getNoteObservableById({ noteId: item.locator })
        : undefined;
    const tasks = (item?.task_ids ?? []).flatMap(taskId => {
      const task = store.assistantTasks.getCurrentAssistantModelById(taskId);
      return task ? [task] : [];
    });
    const toggleTaskComplete = async ({ taskId }: { taskId: string }) => {
      const task = store.assistantTasks.getCurrentAssistantModelById(taskId);
      if (!task) return;

      const status = normalizeAssistantTaskStatus({ status: task.status });
      await task.updateStatus({
        status: isClosedAssistantTaskStatus({ status })
          ? AssistantTaskStatus.Pending
          : AssistantTaskStatus.Completed,
      });
    };

    return (
      <AgentPageLayout activeTab={AgentSurfaceNavigationActiveTab.Information}>
        <AgentBackLink
          label="Back to Information"
          onClick={() => store.agentOverlay.openInformation()}
        />
        {graph.isLoading ? (
          <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
            Loading information…
          </MDSText>
        ) : !item ? (
          <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
            Information item not found.
          </MDSText>
        ) : (
          <StyledStack>
            <MDSText
              color={MDSTextColor.Primary}
              variant={MDSTextVariant.HeaderM}
              weight={MDSTextWeight.SemiBold}
            >
              {item.descriptor}
            </MDSText>
            <AgentMetaCard
              items={[
                {
                  label: "State",
                  value: item.lifecycle === "archived" ? "Archived" : "Active",
                },
                { label: "Kind", value: item.kind },
                { label: "Source ID", value: item.locator },
                { label: "Observed version", value: item.observed_version },
                { label: "Connection", value: item.connection_id || "Mem" },
                { label: "Why", value: item.lifecycle_rationale || "—" },
                {
                  label: "Confidence",
                  value:
                    item.lifecycle_confidence === null || item.lifecycle_confidence === undefined
                      ? "—"
                      : `${Math.round(item.lifecycle_confidence * 100)}%`,
                },
                {
                  label: "Archived",
                  value: item.archived_at
                    ? formatAgentDate({
                        value: item.archived_at,
                        includeTime: true,
                      })
                    : "—",
                },
              ]}
            />
            <AgentRelationshipCard title="Connected work">
              {item.kind === "note" && note?.isAvailable && (
                <AgentRelationshipSection>
                  <AgentRelationshipLabel>Source note</AgentRelationshipLabel>
                  <AgentSourceNoteCard
                    note={note}
                    onOpen={({ noteId }) => store.agentOverlay.openSourceNote({ noteId })}
                  />
                </AgentRelationshipSection>
              )}
              {item.url && (
                <AgentRelationshipSection>
                  <AgentRelationshipLabel>Source</AgentRelationshipLabel>
                  <AgentLinkButton
                    onClick={() => windowModule.openInNewTab({ url: new URL(item.url as string) })}
                  >
                    Open connected record
                  </AgentLinkButton>
                </AgentRelationshipSection>
              )}
              <AgentRelationshipSection>
                <AgentRelationshipLabel>Linked tasks</AgentRelationshipLabel>
                <AgentLinkedTaskList
                  tasks={tasks}
                  onOpenTask={({ taskId }) => store.agentOverlay.openTask({ taskId })}
                  onToggleTaskComplete={toggleTaskComplete}
                />
              </AgentRelationshipSection>
            </AgentRelationshipCard>
          </StyledStack>
        )}
      </AgentPageLayout>
    );
  }
);

const StyledStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
`;
